// import { Alert, AlertDescription } from "@/components/ui/alert";

import { format } from "date-fns";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { EmailPreview } from "../components/EmailPreview";
import { Layout } from "../components/Layout";
import { Tabs } from "../components/Tabs";
import { EmailTemplate, Notification } from "../models/notification";
import { UserInfo } from "../models/user-info";
import notificationService from "../services/notification-service";

interface AcademicEmailStatusProps {
  userInfo: UserInfo;
}

const AcademicEmailStatus = (props: AcademicEmailStatusProps) => {
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>();
  const [emailTemplate, setEmailTemplate] = useState<EmailTemplate>();
  const [notification, setNotification] = useState<Notification>();

  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    notificationService
      .getUserNotifications()
      .then((res) => {
        if (res?.items?.length) {
          const emailNotifications = res?.items?.filter(
            (notification) => notification.EmailNotification,
          );
          setNotifications(emailNotifications);
          if (emailNotifications?.[0]) {
            return previewEmailTemplate(emailNotifications?.[0]);
          }
        }
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
      });
  }, []);

  const getNotifications = async () => {
    const res = await notificationService.getUserNotifications();
    setNotifications(res?.items);
  };

  const previewEmailTemplate = async (notification: Notification) => {
    setNotification(notification);
    setLoading(true);
    const emailTemplate = await notificationService.getEmailTemplate(
      notification.EmailTemplate,
    );
    setEmailTemplate(emailTemplate);
    setLoading(false);
  };

  const handleRevoke = async (uuid: string) => {
    if (
      window.confirm(
        "Are you sure you would like to remove this notification from the send queue?",
      )
    ) {
      if (uuid) {
        setLoading(true);
        await notificationService.revokeNotification(uuid);
        await getNotifications();
        setLoading(false);
      }
    }
  };

  const handleTabClick = (tab: string) => {
    if (tab === "Compose an Email") {
      navigate("/academic-compose-email");
    }
  };

  const statusClass = (status: string) => {
    switch (status) {
      case "sent":
      case "approved":
        return "bg-[#E6F6F3] text-green-800";
      case "rejected":
      case "revoked":
        return "bg-[#FEE2E2] text-red-800";
      default:
        return "bg-[#F3F4F6] text-gray-500";
    }
  };

  return (
    <Layout
      userInfo={props.userInfo}
      hideNavigationLinks
      loading={loading}
      className="flex flex-col h-dvh bg-light"
    >
      <div className="flex flex-col pt-8 px-10 ">
        {/* Header */}
        <div className="mb-8 w-full">
          <Tabs
            items={[
              { title: "Compose an Email" },
              { title: "Status", active: true },
            ]}
            onClick={handleTabClick}
            className="mb-8"
          />
        </div>

        {/* Content */}
        <div className="grid grid-cols-2 gap-4">
          <div className="max-h-screen overflow-y-auto">
            <ul className="w-full">
              {notifications?.map((notification) => (
                <li
                  key={notification.uuid}
                  className="pb-3 sm:pb-4 bg-white rounded-md shadow-md p-4 mb-2 cursor-pointer"
                  onClick={() => previewEmailTemplate(notification)}
                >
                  <div className="flex space-x-4 rtl:space-x-reverse">
                    <div className="flex-1 min-w-0">
                      <h2 className="font-bold text-gray-950 truncate">
                        {notification.Title}
                      </h2>
                      <p className="text-xs text-gray-500 truncate mt-2 flex">
                        <span className="pr-5">
                          {format(new Date(notification.SendAt), "eee, dd MMM")}
                        </span>
                        {notification.EmailTemplate && (
                          <span className="pl-5 border-l-2">
                            {notification.EmailTemplate}
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="inline-flex items-start text-base text-gray-900 dark:text-white align-top flex-col">
                      <span
                        className={`flex align-top items-center gap-1 p-1 text-xs rounded px-2 py-0.5
                            ${statusClass(notification.Status)}`}
                      >
                        <span className="capitalize">
                          {notification.Status}
                        </span>
                      </span>
                      {notification.Status === "pending" && (
                        <button
                          type="button"
                          onClick={() => handleRevoke(notification.uuid)}
                          className="bg-red-600 text-white text-xs rounded px-2 py-1 mt-2"
                        >
                          Reject
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          <div className="flex flex-col max-h-screen">
            {emailTemplate && (
              <EmailPreview
                emailTemplate={emailTemplate}
                emailTemplateVariables={{
                  ...notification?.EmailTemplateVariables,
                  EmailSignature: notification?.EmailSignature,
                  PreferredName: "{{PreferredName}}",
                }}
                useEditorStyle={false}
              />
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AcademicEmailStatus;
